import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { StepUpCodeField } from './StepUpCodeField';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import { consentLabel, countryLabel, languageLabel } from '../lib/contactAttributes';
import { formatDateTime, formatDateWithYear } from '../lib/format';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import {
  ApiError,
  type AddressOrders,
  type BounceContactOverview,
  type BounceDetail,
  type BounceResendOffer,
  type BounceResendResponse,
  type BounceResendResult,
  type FixAddressResponse,
} from '../types/api';

/** "None", or "2 orders, latest 28 Aug 2026". */
export function describeOrders(o: AddressOrders): string {
  if (o.count === 0) return 'None';
  const what = o.count === 1 ? '1 order' : `${o.count} orders`;
  return o.lastOrderedAt === null ? what : `${what}, latest ${formatDateWithYear(o.lastOrderedAt)}`;
}

const FIX_CONFLICT_TEXT: Record<string, string> = {
  address_taken: 'Another contact already has the corrected address. Nothing was changed.',
  address_blocked:
    'The corrected address is blocked itself (unsubscribed, bounced or marked as spam). Nothing was changed.',
  contact_blocked:
    'The contact unsubscribed or marked a mail as spam, so the address is not moved. Nothing was changed.',
  contact_missing: 'No contact has the bounced address any more (already fixed or deleted).',
  not_a_typo: 'This bounce is not a typo, so there is nothing to fix.',
};

function fixErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 403) return 'Only an administrator can do this.';
  if (err instanceof ApiError && err.status === 404) return 'This bounce no longer exists.';
  if (err instanceof ApiError && err.status === 409)
    return FIX_CONFLICT_TEXT[err.body.error] ?? 'The address could not be fixed.';
  return 'The address could not be fixed. Try again.';
}

const NOT_SENT_TEXT: Record<string, string> = {
  already_resent: 'It has already been sent to the new address.',
  not_claimable: 'It has already been sent to the new address.',
  too_old: 'The newsletter is more than 14 days old, so it is not sent again.',
  campaign_not_resendable: 'The newsletter is more than 14 days old, so it is not sent again.',
  contact_unavailable: 'The contact unsubscribed or was deleted.',
  recipient_skipped:
    'The contact can’t get this newsletter (unsubscribed, blocked or another language).',
  kill_switch: 'All sending is stopped right now (kill switch).',
  sender_paused: 'The sender is paused.',
  sender_halted: 'The mail service stopped the sender.',
  no_allowance: 'Today’s sending limit is used up. Try again tomorrow.',
  missing_company_address: 'The brand kit has no company address.',
  missing_legal_notice_url: 'The brand kit has no legal notice link.',
  sender_not_verified: 'The sender’s domain is not verified.',
  coupon_campaign:
    'The newsletter gave each recipient a personal discount code locked to their old address, so it is not sent again.',
  coupon_shop_not_connected: 'The shop is not connected.',
  rejected: 'The mail service turned the mail down.',
};

/** What happened to the resend, in one sentence; null when it went out. */
/**
 * After a fix or a send: the side list's counts and this row's facts. The
 * list itself is left as loaded, so the row and what happened to it stay in
 * view; a typo dealt with moves to "Typo corrected" when the list next loads.
 */
function refreshBounceFacts(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === 'suppressions' &&
      queryKey[1] === 'bounces' &&
      (queryKey[2] === 'summary' || queryKey[2] === 'contact'),
  });
}

export function resendOutcomeText(result: BounceResendResult): string | null {
  if (result.status === 'sent') return null;
  if (result.status === 'unknown')
    return 'The mail service gave no clear answer, so it may have gone out. It is not sent again.';
  return `Nothing was sent. ${NOT_SENT_TEXT[result.reason] ?? 'Try again later.'}`;
}

/** "“Halloween 2026 (DE)”, sent to everyone else on 26 Oct, 16:12". */
function OfferSummary({ offer }: { offer: BounceResendOffer }) {
  return (
    <>
      <span className="font-medium">“{offer.campaign.name}”</span>
      <span className="block text-muted">
        Went to everyone else on {formatDateTime(offer.campaign.sentAt)}.
      </span>
    </>
  );
}

/**
 * Gives the contact the corrected address (admin, step-up). When the newsletter that
 * bounced can still go, the admin chooses: send it now, or send nothing (the contact gets
 * the next newsletter like everyone else). The bounced address stays blocked.
 */
function FixAddressDialog({
  bounceId,
  from,
  to,
  offer,
}: {
  bounceId: string;
  from: string;
  to: string;
  offer: BounceResendOffer | null;
}) {
  const [open, setOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [sendLatest, setSendLatest] = useState(false);
  const [notSent, setNotSent] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const canResend = offer !== null && offer.blocked === null;

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<FixAddressResponse>(`/suppressions/bounces/${bounceId}/fix-address`, {
        method: 'POST',
        body: {
          ...(stepUp.active ? {} : stepUpBody(totpCode)),
          sendLatest: canResend && sendLatest,
        },
      }),
    onSuccess: (data) => {
      stepUp.onStepUpSuccess();
      setTotpCode('');
      // The address is fixed either way; keep the dialog open only to say the send didn't go.
      // The refetch waits for the dialog to close: the fixed row no longer renders this dialog.
      const outcome = data.resend ? resendOutcomeText(data.resend) : null;
      if (outcome) setNotSent(outcome);
      else {
        setOpen(false);
        refreshBounceFacts(queryClient);
      }
    },
    onError: stepUp.onStepUpError,
  });

  const canConfirm = (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          if (notSent) refreshBounceFacts(queryClient);
          setTotpCode('');
          setSendLatest(false);
          setNotSent(null);
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="primary" size="sm" className="self-start">
          Fix address
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Fix address"
        description="The contact gets the corrected address. The bounced address stays blocked."
      >
        {notSent ? (
          <div className="flex flex-col gap-4">
            <Alert>The address is fixed. {notSent}</Alert>
            <DialogFooter>
              <DialogClose>
                <Button type="button" variant="primary" className="h-11">
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canConfirm) mutation.mutate();
            }}
          >
            <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
              <dt className="text-faint">From</dt>
              <dd className="m-0 min-w-0 break-all">{from}</dd>
              <dt className="text-faint">To</dt>
              <dd className="m-0 min-w-0 font-medium break-all">{to}</dd>
            </dl>
            {canResend ? (
              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0 text-sm">
                <legend className="mb-1 p-0 font-medium">The newsletter that bounced</legend>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="fix-address-send"
                    className="mt-1"
                    checked={sendLatest}
                    onChange={() => setSendLatest(true)}
                  />
                  <span>
                    Send <OfferSummary offer={offer} />
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="fix-address-send"
                    className="mt-1"
                    checked={!sendLatest}
                    onChange={() => setSendLatest(false)}
                  />
                  <span>
                    Send nothing now
                    <span className="block text-muted">They get the next newsletter.</span>
                  </span>
                </label>
              </fieldset>
            ) : (
              <p className="m-0 text-sm text-muted">
                Nothing is sent now: the next newsletter goes to the new address.
              </p>
            )}
            {mutation.isError ? <Alert>{fixErrorText(mutation.error)}</Alert> : null}
            <StepUpCodeField
              id="fix-address-code"
              value={totpCode}
              onChange={setTotpCode}
              active={stepUp.active}
              minutesLeft={stepUp.minutesLeft}
            />
            <DialogFooter>
              <DialogClose>
                <Button type="button" variant="secondary" className="h-11">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" variant="primary" disabled={!canConfirm} className="h-11">
                {mutation.isPending
                  ? 'Fixing…'
                  : canResend && sendLatest
                    ? 'Fix and send'
                    : 'Fix address'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function resendErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 403) return 'Only an administrator can do this.';
  if (err instanceof ApiError && err.status === 404) return 'There is no newsletter to send again.';
  return 'The newsletter could not be sent. Try again.';
}

/** For a bounce already fixed: send the newsletter that bounced, once, to the new address. */
function ResendDialog({
  bounceId,
  to,
  offer,
}: {
  bounceId: string;
  to: string;
  offer: BounceResendOffer;
}) {
  const [open, setOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [notSent, setNotSent] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<BounceResendResponse>(`/suppressions/bounces/${bounceId}/resend`, {
        method: 'POST',
        body: stepUp.active ? {} : stepUpBody(totpCode),
      }),
    onSuccess: (data) => {
      stepUp.onStepUpSuccess();
      setTotpCode('');
      const outcome = resendOutcomeText(data.resend);
      if (outcome) setNotSent(outcome);
      else {
        setOpen(false);
        refreshBounceFacts(queryClient);
      }
    },
    onError: stepUp.onStepUpError,
  });

  const canConfirm = (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          if (notSent) refreshBounceFacts(queryClient);
          setTotpCode('');
          setNotSent(null);
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="primary" size="sm" className="self-start">
          Send latest newsletter
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Send latest newsletter"
        description="The newsletter that bounced goes once to the new address."
      >
        {notSent ? (
          <div className="flex flex-col gap-4">
            <Alert>{notSent}</Alert>
            <DialogFooter>
              <DialogClose>
                <Button type="button" variant="primary" className="h-11">
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canConfirm) mutation.mutate();
            }}
          >
            <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
              <dt className="text-faint">Newsletter</dt>
              <dd className="m-0 min-w-0">
                <OfferSummary offer={offer} />
              </dd>
              <dt className="text-faint">To</dt>
              <dd className="m-0 min-w-0 font-medium break-all">{to}</dd>
            </dl>
            {mutation.isError ? <Alert>{resendErrorText(mutation.error)}</Alert> : null}
            <StepUpCodeField
              id="resend-code"
              value={totpCode}
              onChange={setTotpCode}
              active={stepUp.active}
              minutesLeft={stepUp.minutesLeft}
            />
            <DialogFooter>
              <DialogClose>
                <Button type="button" variant="secondary" className="h-11">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" variant="primary" disabled={!canConfirm} className="h-11">
                {mutation.isPending ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-faint">{label}</dt>
      <dd className="m-0 min-w-0 break-words">{children}</dd>
    </>
  );
}

/**
 * The open bounce row's contact: when and how they signed up, their orders and, for a
 * typo, the corrected address's orders, so an admin can judge the fix before making it.
 */
export function BounceContactPanel({ bounce }: { bounce: BounceDetail }) {
  const { isAdmin } = useAuth();
  const query = useQuery({
    queryKey: ['suppressions', 'bounces', 'contact', bounce.id],
    queryFn: ({ signal }) =>
      apiFetch<BounceContactOverview>(`/suppressions/bounces/${bounce.id}/contact`, { signal }),
  });

  if (query.isLoading) return <p className="m-0 text-xs text-muted">Loading the contact…</p>;
  if (query.isError || !query.data)
    return <p className="m-0 text-xs text-muted">The contact could not be loaded.</p>;

  const { contact, orders, corrected, resend } = query.data;
  const fixed = contact === null && corrected?.contactId != null;
  const taken = contact !== null && corrected?.contactId != null;

  return (
    <section aria-label="Contact" className="flex flex-col gap-2 border-t border-border-soft pt-2">
      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {contact ? (
          <>
            <Row label="Signed up">
              {formatDateWithYear(contact.signedUpAt ?? contact.createdAt)}
              {contact.source ? <span className="text-muted"> · {contact.source}</span> : null}
            </Row>
            <Row label="In NP-Flow since">{formatDateWithYear(contact.createdAt)}</Row>
            <Row label="Consent">
              {consentLabel(contact.consentCategory, contact.orderSignupAt)}
            </Row>
            <Row label="Language">
              {languageLabel(contact.language)} · {countryLabel(contact.country)}
            </Row>
          </>
        ) : (
          <Row label="Contact">
            {fixed
              ? 'Fixed: the contact now has the corrected address.'
              : 'Not a contact (any more).'}
          </Row>
        )}
        <Row label="Orders">{describeOrders(orders)}</Row>
        {corrected ? (
          <Row label={fixed ? 'New address' : 'Corrected address'}>
            <span className="break-all">{corrected.email}</span>
            <span className="block text-muted">Orders: {describeOrders(corrected.orders)}</span>
            {taken ? (
              <span className="block text-muted">
                Already a contact, so it can’t be moved there.
              </span>
            ) : null}
          </Row>
        ) : null}
        {fixed && resend ? (
          <Row label="Newsletter">
            “{resend.campaign.name}”
            <span className="block text-muted">
              {resend.blocked === 'already_resent'
                ? 'Sent to the new address.'
                : resend.blocked === null
                  ? `Bounced. It went to everyone else on ${formatDateTime(resend.campaign.sentAt)}.`
                  : 'Not sent to the new address. They get the next newsletter.'}
            </span>
          </Row>
        ) : null}
      </dl>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {contact ? (
          <Link to={`/kontakter/${contact.id}`} className="text-link hover:underline">
            Open contact
          </Link>
        ) : null}
        {corrected?.contactId ? (
          <Link to={`/kontakter/${corrected.contactId}`} className="text-link hover:underline">
            {fixed ? 'Open contact' : 'Open the contact with the corrected address'}
          </Link>
        ) : null}
      </div>
      {isAdmin && contact && corrected && !taken ? (
        <FixAddressDialog
          bounceId={bounce.id}
          from={bounce.email}
          to={corrected.email}
          offer={resend}
        />
      ) : null}
      {isAdmin && fixed && corrected && resend?.blocked === null ? (
        <ResendDialog bounceId={bounce.id} to={corrected.email} offer={resend} />
      ) : null}
    </section>
  );
}
